import DeviceRow from "../components/DevicePage/DeviceRow";
import Pagination from "../components/DevicePage/Pagination";
import FilterBlock from "../components/DevicePage/FilterBlock";
import FilterApplyBar from "../components/DevicePage/FilterApplyBar";
import {usePhoneFilters} from "../hooks/usePhoneFilters";
import {Device} from "../types/device";
import {useAllBrands} from "../api/client";
import {X} from "lucide-react";
import {DeviceFilters} from "../types/filters";
import getRangeLabel from "../utils/ranges";

export default function Phones() {
    const { data: allBrands = [] } = useAllBrands(); // Fetch the full list
    const {
        isLoading,
        error,
        currentTableData,
        currentPage,
        maxPage,
        pendingFilters,
        filteredDevicesCount,
        popoverPos,
        totalCount,
        isApplyBarVisible,
        filterRef,
        activeFilters,
        availableBrands,
        handlePageChange,
        handleFilterChange,
        applyFilters,
        handleClearFilter,
        closeApplyBar,
    } = usePhoneFilters();


    return (
        <div className="min-h-screen flex flex-col">
            <main className="flex-1 bg-[#F6F6F6]">
                <div className="max-w-7xl mx-auto px-6 py-10">
                    <div className="flex justify-center">
                        <div className="grid grid-cols-3 gap-x-16 items-start">

                            {/* Device List */}
                            <div className="col-span-2 w-full max-w-3xl flex flex-col gap-6">
                                <h2 className="text-xl font-semibold text-slate-700 tracking-tight">Found {totalCount} devices:</h2>
                                <div className="flex flex-wrap gap-2 mb-6">
                                    {Object.entries(activeFilters).map(([key, value]) => {
                                        const filterKey = key as keyof DeviceFilters;

                                        // Skip query ('q') or empty values
                                        if (!value || key === 'q') return null;

                                        // If it's an array (brands, ram, etc.), map each item
                                        if (Array.isArray(value)) {
                                            return (value as any[]).map((item, index) => {

                                                let displayValue = "";
                                                if (Array.isArray(item)) {
                                                    // Try to find the pretty label in our Maps
                                                    const label = getRangeLabel(key, item);
                                                    displayValue = label || `${item[0]}-${item[1]}`; // Fallback to numbers
                                                } else {
                                                    displayValue = item; // It's a brand string
                                                }

                                                return (
                                                    <div key={`${key}-${index}`} className="flex items-center gap-2 px-3 py-1 bg-white border border-slate-200 rounded-full text-slate-700 text-sm shadow-sm">
                                                        <span className="text-slate-400 capitalize">{key.replace('_', ' ')}:</span>
                                                        <span className="font-medium">{displayValue}</span>
                                                        <button
                                                            onClick={() => {
                                                                const updated = handleFilterChange(filterKey, item, null);
                                                                applyFilters(updated);
                                                            }}
                                                            className="hover:text-red-600 transition-colors ml-1"
                                                        >
                                                            <X size={14} />
                                                        </button>
                                                    </div>
                                                );
                                            });
                                        }

                                        // Handle booleans (5G, NFC, etc.)
                                        if (typeof value === "boolean" && value) {
                                            return (
                                                <div key={key} className="flex items-center gap-2 px-3 py-1 bg-white border border-slate-200 rounded-full text-slate-700 text-sm shadow-sm">
                                                    <span className="font-medium capitalize">{key.replace('has_', '').replace('_', ' ')}</span>
                                                    <button
                                                        onClick={() => {
                                                            const updated = handleFilterChange(filterKey, false, null);
                                                            applyFilters(updated); // Consistent with array logic
                                                        }}
                                                        className="hover:text-red-600 transition-colors ml-1"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            );
                                        }

                                        return null;
                                    })}
                                </div>
                                    {isLoading ? (
                                    <div>Loading...</div>
                                ) : error ? (
                                    <div>Error loading devices</div>
                                ) : (
                                    currentTableData.map((device: Device) => (
                                        <DeviceRow key={device.id} device={device}/>
                                    ))
                                )}

                                {!isLoading && currentTableData.length === 0 && (
                                    <div className="p-4 text-gray-500">
                                        No devices match your filters.
                                    </div>
                                )}
                            </div>
                            {/* Filters */}
                            <FilterBlock
                                ref={filterRef}
                                allBrands={allBrands}
                                availableBrands={availableBrands}
                                activeFilters={pendingFilters}
                                onFilterChange={handleFilterChange}
                                onClearFilter={handleClearFilter}
                            />
                        </div>
                    </div>

                    <Pagination
                        currentPage={currentPage + 1}
                        totalPages={maxPage}
                        onPageChange={(page) => handlePageChange(page - 1)}
                    />

                    <FilterApplyBar
                        position={popoverPos}
                        visible={isApplyBarVisible}
                        selectedCount={filteredDevicesCount ?? 0}
                        onApply={()=>applyFilters()}
                        onClose={closeApplyBar}
                    />
                </div>
            </main>
        </div>
    );
}